import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { useRevealAccountPassword } from '@/hooks/queries';
import { useToast } from '@/hooks/useToast';
import { IconCheck, IconCopy, IconEye, IconEyeOff } from '@/components/Icons';
import { copyText } from '@/lib/clipboard';
import type { LeverageLead } from '@/lib/types';

/** O mesmo tempo da ficha do cliente: a senha aberta some sozinha. */
const SHOWN_MS = 30_000;
const COPIED_MS = 2_000;

/**
 * A conta do cliente na corretora, na linha da execução: o login com copiar e
 * a senha mascarada, buscada no servidor só quando alguém pede para mostrar ou
 * copiar — cada abertura fica na auditoria.
 */
export function AccountCell({ lead }: { lead: LeverageLead }) {
  const toast = useToast();
  const reveal = useRevealAccountPassword();
  const [shown, setShown] = useState<string | null>(null);
  const [copied, setCopied] = useState<'login' | 'password' | null>(null);
  const hideTimer = useRef<number | undefined>(undefined);
  const copiedTimer = useRef<number | undefined>(undefined);

  useEffect(
    () => () => {
      window.clearTimeout(hideTimer.current);
      window.clearTimeout(copiedTimer.current);
    },
    [],
  );

  async function fetchPassword(): Promise<string | null> {
    try {
      return (await reveal.mutateAsync(lead.id)).password;
    } catch (caught) {
      toast.fromError(caught, 'Não foi possível abrir a senha.');
      return null;
    }
  }

  async function toggle() {
    window.clearTimeout(hideTimer.current);
    if (shown !== null) return setShown(null);
    const password = await fetchPassword();
    if (password === null) return;
    setShown(password);
    hideTimer.current = window.setTimeout(() => setShown(null), SHOWN_MS);
  }

  async function copy(kind: 'login' | 'password', event: MouseEvent<HTMLButtonElement>) {
    const container = event.currentTarget.closest<HTMLElement>('[role="dialog"]') ?? undefined;
    const value = kind === 'login' ? lead.accountLogin : (shown ?? (await fetchPassword()));
    if (!value) return;
    if (!(await copyText(value, container))) {
      toast.error('Não foi possível copiar. Mostre e copie à mão.');
      return;
    }
    setCopied(kind);
    window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setCopied(null), COPIED_MS);
  }

  return (
    <div className="lev-cred">
      <div className="lev-cred-line">
        {lead.accountLogin ? (
          <>
            <span className="lev-cred-value truncate" title={lead.accountLogin}>{lead.accountLogin}</span>
            <button
              type="button"
              className="icon-btn lev-cred-btn"
              aria-label={`Copiar o login de ${lead.name}`}
              title={copied === 'login' ? 'Copiado' : 'Copiar login'}
              onClick={(event) => void copy('login', event)}
            >
              {copied === 'login' ? <IconCheck /> : <IconCopy />}
            </button>
          </>
        ) : (
          <span className="lev-cred-missing">Sem login</span>
        )}
      </div>
      <div className="lev-cred-line">
        {lead.hasAccountPassword ? (
          <>
            <span className={`lev-cred-value${shown === null ? ' is-masked' : ''}`}>
              {shown === null ? <span aria-label="Senha escondida">••••••••</span> : shown}
            </span>
            <button
              type="button"
              className="icon-btn lev-cred-btn"
              disabled={reveal.isPending}
              aria-label={shown === null ? `Mostrar a senha de ${lead.name}` : 'Esconder a senha'}
              title={shown === null ? 'Mostrar senha' : 'Esconder'}
              onClick={() => void toggle()}
            >
              {shown === null ? <IconEye /> : <IconEyeOff />}
            </button>
            <button
              type="button"
              className="icon-btn lev-cred-btn"
              disabled={reveal.isPending}
              aria-label={`Copiar a senha de ${lead.name}`}
              title={copied === 'password' ? 'Copiada' : 'Copiar senha'}
              onClick={(event) => void copy('password', event)}
            >
              {copied === 'password' ? <IconCheck /> : <IconCopy />}
            </button>
          </>
        ) : (
          <span className="lev-cred-missing">Sem senha</span>
        )}
      </div>
      <span className="sr-only" aria-live="polite">{copied ? (copied === 'login' ? 'Login copiado.' : 'Senha copiada.') : ''}</span>
    </div>
  );
}
